import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Col, Row, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { CloseOutlined } from '@ant-design/icons';
import { MinimalClientCompany } from '../../bookingOrders/types/ClientCompany';

import { useInsights } from '../../context/insights';
import User, { UserRole } from '../../user/types/User';
import UserAPI from '../../user/UserAPI';
import { useUser } from '../../user/userContext';
import Course from '../../course/types/Course';
import CourseAPI from '../../course/CourseAPI';
import InvoiceRecipientAPI from '../../bookingOrders/api/InvoiceRecipientAPI';
import InvoiceRecipient from '../../bookingOrders/types/InvoiceRecipient';
import SubsidiaryCompany from '../../subsidiaryCompany/types/SubsidiaryCompany';
import SubsidiaryCompanyAPI, {
  GetSubsidiaryCompanyParams,
} from '../../subsidiaryCompany/SubsidiaryCompanyAPI';
import ClientCompanySelectOption from '../../clientCompany/ClientCompanySelectOption';
import SearchLoadingIndicator from '../../components/SearchLoadingIndicator';
import useDebounce from '../../utils/useDebounce';
import ClientCompanyAPI from '../../bookingOrders/api/ClientCompanyAPI';
import useInstructorOptions from '../../user/hooks/useInstructorOptions';

type InsightsExtendedFiltersProps = {
  show: boolean;
  close: () => void;
};

const InsightsExtendedFilters: React.FC<InsightsExtendedFiltersProps> = ({
  show,
  close,
}) => {
  const { t } = useTranslation();
  const [currentUser] = useUser();

  const {
    market,
    subsidiaryCompanyIds,
    setSubsidiaryCompanyIds,
    clientCompanyIds,
    setClientCompanyIds,
    instructorIds,
    setInstructorIds,
    courseIds,
    setCourseIds,
    responsibleBookerIds,
    setResponsibleBookerIds,
    invoiceRecipientIds,
    setInvoiceRecipientIds,
  } = useInsights();

  const [subsidiaryCompanies, setSubsidiaryCompanies] =
    useState<SubsidiaryCompany[]>();
  const [loadingSubsidiaryCompanies, setLoadingSubsidiaryCompanies] =
    useState(false);

  const [clientCompanies, setClientCompanies] =
    useState<MinimalClientCompany[]>();

  const [courses, setCourses] = useState<Course[]>();
  const [loadingCourses, setLoadingCourses] = useState(false);

  const [bookers, setBookers] = useState<User[]>();
  const [loadingBookers, setLoadingBookers] = useState(false);

  const [invoiceRecipients, setInvoiceRecipients] =
    useState<InvoiceRecipient[]>();
  const [loadingInvoiceRecipients, setLoadingInvoiceRecipients] =
    useState(false);
  const [clientCompanySearch, setClientCompanySearch] = useState('');
  const [invoiceRecipientSearch, setInvoiceRecipientSearch] = useState('');

  const {
    hasMoreInstructors,
    instructors,
    loadFirstInstructorPage,
    loadMoreInstructors,
    loadingInstructorOptions,
    loadingInstructors,
    searchInstructors,
  } = useInstructorOptions({
    hydrateSelectedIds: instructorIds,
  });

  const fetchSubsidiaryCompanies = useCallback(
    async (params: GetSubsidiaryCompanyParams) => {
      setLoadingSubsidiaryCompanies(true);
      try {
        const { data } =
          await SubsidiaryCompanyAPI.getSubsidiaryCompanies(params);
        setSubsidiaryCompanies(data);
      } finally {
        setLoadingSubsidiaryCompanies(false);
      }
    },
    [],
  );

  const fetchCourses = useCallback(async () => {
    setLoadingCourses(true);
    try {
      const { data } = await CourseAPI.getCourses({ marketId: market?.id });
      setCourses(data);
    } finally {
      setLoadingCourses(false);
    }
  }, [market?.id]);

  const fetchBookers = useCallback(async () => {
    setLoadingBookers(true);
    try {
      const { data } = await UserAPI.getUsers({
        roles: [UserRole.Admin, UserRole.Booker],
      });
      setBookers(data);
    } finally {
      setLoadingBookers(false);
    }
  }, []);

  const fetchInvoiceRecipients = useCallback(async () => {
    setLoadingInvoiceRecipients(true);
    try {
      const { data } = await InvoiceRecipientAPI.getInvoiceRecipients();
      setInvoiceRecipients(data.data);
    } finally {
      setLoadingInvoiceRecipients(false);
    }
  }, []);

  const {
    debounce: clientCompanyDebounce,
    loading: loadingClientCompanyDebounce,
  } = useDebounce(
    (value: string) => {
      if (!currentUser) return;

      return ClientCompanyAPI.getClientCompanies({
        name: value || undefined,
        subsidiaryCompanyIds,
      });
    },
    (res) => setClientCompanies(res.data.data),
    {
      beforeDebounce: () => setClientCompanies([]),
      dependencies: [currentUser, subsidiaryCompanyIds],
    },
  );

  const {
    debounce: invoiceRecipientDebounce,
    loading: loadingInvoiceRecipientDebounce,
  } = useDebounce(
    (value: string) => {
      if (!currentUser) return;

      return InvoiceRecipientAPI.getInvoiceRecipients({
        name: value || undefined,
        subsidiaryCompanyIds,
      });
    },
    (res) => setInvoiceRecipients(res.data.data),
    {
      beforeDebounce: () => setInvoiceRecipients([]),
      dependencies: [currentUser, subsidiaryCompanyIds],
    },
  );

  useEffect(() => {
    fetchCourses();
    fetchBookers();
    fetchInvoiceRecipients();
  }, [fetchBookers, fetchCourses, fetchInvoiceRecipients]);

  useEffect(() => {
    if (!market?.id) {
      return;
    }
    fetchSubsidiaryCompanies({
      marketId: market?.id,
      allMarkets: false,
    });
    setClientCompanySearch('');
    setInvoiceRecipientSearch('');
  }, [fetchSubsidiaryCompanies, market?.id]);

  useEffect(() => {
    if (!subsidiaryCompanies || subsidiaryCompanies.length === 0) {
      return;
    }

    clientCompanyDebounce(clientCompanySearch);
  }, [
    clientCompanySearch,
    subsidiaryCompanies,
    subsidiaryCompanyIds,
    market?.id,
    clientCompanyDebounce,
  ]);

  useEffect(() => {
    if (!subsidiaryCompanies || subsidiaryCompanies.length === 0) {
      return;
    }
    invoiceRecipientDebounce(invoiceRecipientSearch);
  }, [
    invoiceRecipientDebounce,
    invoiceRecipientSearch,
    subsidiaryCompanies,
    subsidiaryCompanyIds,
  ]);

  const clearFilters = useCallback(() => {
    setSubsidiaryCompanyIds(undefined);
    setClientCompanyIds(undefined);
    setInstructorIds(undefined);
    setCourseIds(undefined);
    setResponsibleBookerIds(undefined);
    setInvoiceRecipientIds(undefined);
  }, [
    setClientCompanyIds,
    setCourseIds,
    setInstructorIds,
    setInvoiceRecipientIds,
    setResponsibleBookerIds,
    setSubsidiaryCompanyIds,
  ]);

  if (!show) {
    return null;
  }

  return (
    <Card className="relative w-full border-1 border-solid border-gray-500">
      <Button
        icon={<CloseOutlined />}
        onClick={close}
        className="absolute top-[16px] right-[16px]"
      />

      <Row className="text-black text-xl font-semibold">
        {t('components.InsightsExtendedFilters.title')}
      </Row>

      <Row gutter={[24, 12]} className="my-4">
        <Filter
          value={subsidiaryCompanyIds}
          setValue={setSubsidiaryCompanyIds}
          label={t(
            'components.InsightsExtendedFilters.subsidiaryCompaniesLabel',
          )}
          placeholder={t(
            'components.InsightsExtendedFilters.subsidiaryCompaniesSelect',
          )}
          loading={loadingSubsidiaryCompanies}
          options={subsidiaryCompanies}
        />

        <Col span={12} className="flex flex-col gap-1">
          <span className="text-md font-semibold">
            {t('components.InsightsExtendedFilters.clientCompaniesLabel')}
          </span>
          <Select
            value={clientCompanyIds}
            placeholder={t(
              'components.InsightsExtendedFilters.clientCompaniesSelect',
            )}
            loading={loadingClientCompanyDebounce}
            popupMatchSelectWidth={false}
            mode="multiple"
            showSearch
            onSearch={setClientCompanySearch}
            onBlur={() => setClientCompanySearch('')}
            onChange={(value) => {
              setClientCompanyIds(value);
            }}
            filterOption={false}
            notFoundContent={
              loadingClientCompanyDebounce ? <SearchLoadingIndicator /> : null
            }
            allowClear>
            {clientCompanies?.map((cc) => (
              <Select.Option key={cc.id} value={cc.id} filterValue={cc.name}>
                <ClientCompanySelectOption clientCompany={cc} />
              </Select.Option>
            ))}
          </Select>
        </Col>

        <Filter
          value={instructorIds}
          setValue={setInstructorIds}
          label={t('components.InsightsExtendedFilters.instructorsLabel')}
          placeholder={t(
            'components.InsightsExtendedFilters.instructorsSelect',
          )}
          loading={loadingInstructors}
          loadingOptions={loadingInstructorOptions}
          options={instructors}
          hasMore={hasMoreInstructors}
          isDebounced
          onDropdownOpen={() => loadFirstInstructorPage('')}
          onLoadMore={loadMoreInstructors}
          onSearch={searchInstructors}
        />

        <Filter
          value={courseIds}
          setValue={setCourseIds}
          label={t('components.InsightsExtendedFilters.coursesLabel')}
          placeholder={t('components.InsightsExtendedFilters.coursesSelect')}
          loading={loadingCourses}
          options={courses}
        />

        <Filter
          value={responsibleBookerIds}
          setValue={setResponsibleBookerIds}
          label={t(
            'components.InsightsExtendedFilters.responsibleBookersLabel',
          )}
          placeholder={t(
            'components.InsightsExtendedFilters.responsibleBookersSelect',
          )}
          loading={loadingBookers}
          options={bookers}
        />

        <Filter
          value={invoiceRecipientIds}
          setValue={setInvoiceRecipientIds}
          label={t('components.InsightsExtendedFilters.invoiceRecipientsLabel')}
          placeholder={t(
            'components.InsightsExtendedFilters.invoiceRecipientsSelect',
          )}
          loading={loadingInvoiceRecipients || loadingInvoiceRecipientDebounce}
          options={invoiceRecipients}
          onSearch={setInvoiceRecipientSearch}
          onSearchClear={() => setInvoiceRecipientSearch('')}
          isDebounced
        />
      </Row>

      <Row>
        <Button
          type="default"
          onClick={clearFilters}
          disabled={
            (subsidiaryCompanyIds === undefined ||
              subsidiaryCompanyIds.length === 0) &&
            (clientCompanyIds === undefined || clientCompanyIds.length === 0) &&
            (instructorIds === undefined || instructorIds.length === 0) &&
            (courseIds === undefined || courseIds.length === 0) &&
            (responsibleBookerIds === undefined ||
              responsibleBookerIds.length === 0) &&
            (invoiceRecipientIds === undefined ||
              invoiceRecipientIds.length === 0)
          }>
          {t('components.InsightsExtendedFilters.clearButton')}
        </Button>
      </Row>
    </Card>
  );
};

export default InsightsExtendedFilters;

type FilterProps = {
  value: number[] | undefined;
  setValue: (v: number[]) => void;
  label: string;
  placeholder: string;
  loading: boolean;
  loadingOptions?: boolean;
  options?: { id: number | null; name: string }[];
  hasMore?: boolean;
  onDropdownOpen?: () => void;
  onLoadMore?: () => void;
  onSearch?: (value: string) => void;
  onSearchClear?: () => void;
  isDebounced?: boolean;
};

const Filter: React.FC<FilterProps> = ({
  value,
  setValue,
  label,
  placeholder,
  loading,
  loadingOptions,
  options,
  hasMore,
  onDropdownOpen,
  onLoadMore,
  onSearch,
  onSearchClear,
  isDebounced,
}) => {
  return (
    <Col span={12} className="flex flex-col gap-1">
      <span className="text-md font-semibold">{label}</span>

      <Select
        value={value}
        placeholder={placeholder}
        onChange={(e) => {
          setValue(e as number[]);
          if (onSearchClear && isDebounced) {
            onSearchClear();
          }
        }}
        loading={loading}
        popupMatchSelectWidth={false}
        mode="multiple"
        showSearch={isDebounced || !!onSearch}
        onSearch={onSearch}
        onBlur={onSearchClear}
        filterOption={isDebounced || onSearch ? false : true}
        onDropdownVisibleChange={(open) => {
          if (open) {
            onDropdownOpen?.();
          }
        }}
        onPopupScroll={(event) => {
          const target = event.target as HTMLDivElement;
          const isNearBottom =
            target.scrollTop + target.offsetHeight >= target.scrollHeight - 32;

          if (isNearBottom && hasMore) {
            onLoadMore?.();
          }
        }}
        notFoundContent={
          loadingOptions || (isDebounced && loading) ? (
            <SearchLoadingIndicator />
          ) : null
        }
        allowClear>
        {options?.map((o) => (
          <Select.Option key={o.id} value={o.id} filterValue={o.name}>
            {o.name}
          </Select.Option>
        ))}
      </Select>
    </Col>
  );
};
